import { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { STATION_ROLES } from "../constants/roleConstants";

/** Check-bulleted list of one role's capability summaries. */
export function RoleCapabilityList({
  capabilities,
}: {
  capabilities: string[];
}) {
  return (
    <Stack spacing={0.5} sx={{ textAlign: "left" }}>
      {capabilities.map((line) => (
        <Box
          key={line}
          sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}
        >
          <CheckRoundedIcon
            sx={{ fontSize: 16, mt: "3px", color: "success.main" }}
          />
          <Typography variant="body2" color="text.secondary">
            {line}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * Every station role with its capability summary — the reference station
 * admins consult when deciding which role someone needs. Capabilities come
 * from roleConstants, which mirrors the backend's WorkOS role catalog.
 */
export function AllRolesReference() {
  return (
    <Stack spacing={2} sx={{ textAlign: "left" }}>
      {STATION_ROLES.map((role, index) => (
        <Box key={role.slug}>
          {index > 0 && <Divider sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {role.label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.75 }}
          >
            {role.description}
          </Typography>
          <RoleCapabilityList capabilities={role.capabilities} />
        </Box>
      ))}
      <Typography variant="caption" color="text.secondary">
        All roles can use the station's bodycam integration.
      </Typography>
    </Stack>
  );
}

/**
 * Toolbar affordance: an info button that opens the full role reference in a
 * popover, so admins can review what each role grants without opening the
 * change-role dialog.
 */
export function RoleReferenceButton() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title="What each role can do">
        <IconButton
          aria-label="Role permissions reference"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            width: 40,
            height: 40,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            color: "text.secondary",
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              p: 2.5,
              width: 380,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: 480,
              borderRadius: 2,
            },
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Station roles
        </Typography>
        <AllRolesReference />
      </Popover>
    </>
  );
}
